// A modal dialog on the native <dialog> element: focus is trapped, Esc and a backdrop click close it.
// Surface from the shadcn preset: 14px radius, 16px padding, 1px ring, a tinted footer for the actions.

import { useEffect, useId, useRef, type ReactNode } from 'react';
import { IconX } from '@tabler/icons-react';

export function Dialog({ title, onClose, children, wide = false, testId }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean; testId?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (!d.open) d.showModal();
    // land on the first field (or the element marked autofocus), not the close button
    d.querySelector<HTMLElement>('[autofocus], input:not([type=hidden]), select, textarea')?.focus({ preventScroll: true });
    return () => d.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className={`dlg${wide ? ' dlg-wide' : ''}`}
      aria-labelledby={id}
      data-testid={testId}
      onCancel={(e) => {
        // React passes a nested dialog's cancel up its tree: Esc closes only the top one
        if (e.target !== ref.current) return;
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <h2 id={id} className="dlg-title">
        {title}
      </h2>
      <button type="button" className="dlg-close" aria-label="Close" onClick={onClose}>
        <IconX size={16} stroke={1.5} aria-hidden="true" />
      </button>
      <div className="dlg-body">{children}</div>
    </dialog>
  );
}
